import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import PageMeta from "@/components/feature/PageMeta";
import SearchField from "@/components/feature/SearchField";
import SectionHeading from "@/components/feature/SectionHeading";
import ContentCard, { ContentListLink } from "@/components/feature/ContentCard";
import ActorCard from "@/components/feature/ActorCard";
import {
  actors,
  dramas,
  latestDrama,
  newsItems,
  siteInfo,
} from "@/mocks/content";
import { formatDate } from "@/lib/content";

const dramaCategory = `/category/${encodeURIComponent("剧情介绍")}`;
const actorCategory = `/category/${encodeURIComponent("演员资料")}`;
const newsCategory = `/category/${encodeURIComponent("影视资讯")}`;

const stats = [
  { value: "08", label: "剧情档案", hint: "逐集拆解关键情节" },
  { value: "06", label: "演员资料", hint: "主创与角色解读" },
  { value: "06", label: "影视资讯", hint: "收视与幕后动态" },
  { value: "03", label: "内容板块", hint: "剧情 / 演员 / 资讯" },
];

const hotKeywords = ["苏婉", "沈嘉言", "大结局", "餐桌对峙", "林知夏"];

const ranking = [...dramas].slice(-4).reverse();

export default function Home() {
  const featuredNews = newsItems.slice(0, 4);
  const featuredDramas = dramas.slice(0, 4);
  const featuredActors = actors.slice(0, 3);

  return (
    <PageShell>
      <PageMeta
        title="嫂子的诱惑电视剧全集剧情介绍与在线观看"
        description="嫂子的诱惑为您提供完整的电视剧剧情介绍、演员信息和影视资讯，助您深入了解剧情发展，享受精彩电视剧内容。"
        keywords={siteInfo.keywords}
      />

      <section className="relative flex min-h-[560px] w-full items-end overflow-hidden bg-primary-950 md:min-h-[680px]">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20elegant%20old%20Chinese%20mansion%20interior%20at%20dusk%2C%20long%20dining%20table%20with%20candlelight%2C%20deep%20burgundy%20curtains%20and%20warm%20golden%20lamps%2C%20shadowy%20doorway%20in%20background%2C%20moody%20prestige%20drama%20cinematography%2C%20rich%20amber%20and%20crimson%20color%20grading&width=1920&height=1080&seq=home-hero-mansion-01&orientation=landscape"
          alt="嫂子的诱惑 - 沈家老宅剧照"
          title="嫂子的诱惑电视剧全集剧情介绍与在线观看"
          className="absolute inset-0 h-full w-full object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-transparent to-transparent" />

        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 pb-14 pt-28 md:px-6 md:pb-20 md:pt-36 lg:px-10">
          <div className="max-w-3xl animate-fade-up">
            <p className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-accent-300">
              <span className="h-px w-8 bg-accent-400" />
              都市情感 · 全集剧情档案
            </p>
            <h1 className="mt-5 font-heading text-5xl font-black leading-[1.05] text-background-50 md:text-7xl lg:text-[86px]">
              嫂子的诱惑
            </h1>
            <p className="mt-4 font-heading text-base font-medium tracking-[0.16em] text-background-100/90 md:text-xl">
              电视剧全集剧情介绍 · 演员资料 · 影视资讯
            </p>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100/75 md:text-base">
              一屋人家，两代心事。从婚礼上的第一句祝酒词，到手术室外迟来的和解，逐集整理沈家的每一次沉默与开口。
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-2.5">
              {["2026", "都市情感", "全8集", `更新至${latestDrama.chapter}`].map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 text-xs text-background-100/90 backdrop-blur-sm"
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/detail/${latestDrama.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-list-line text-base" />
                查看最新剧情
              </Link>
              <Link
                to={actorCategory}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-50/35 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                <i className="ri-user-star-line text-base" />
                认识演员阵容
              </Link>
            </div>
          </div>

          <div className="mt-10 hidden max-w-md rounded-lg border border-background-50/20 bg-primary-950/50 p-4 backdrop-blur-md lg:block">
            <p className="text-[10px] uppercase tracking-[0.24em] text-accent-300">最新更新</p>
            <Link
              to={`/detail/${latestDrama.id}`}
              className="group mt-2 flex items-center justify-between gap-3"
            >
              <span className="font-heading text-sm font-semibold text-background-50">
                {latestDrama.title}
              </span>
              <i className="ri-arrow-right-up-line text-base text-accent-300 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-background-100/60">
              {latestDrama.excerpt}
            </p>
          </div>
        </div>
      </section>

      <div className="relative z-10 mx-auto -mt-8 w-full max-w-[1280px] px-4 md:-mt-10 md:px-6 lg:px-10">
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-background-200/80 bg-background-50 p-4 md:grid-cols-4 md:p-5">
          {stats.map((stat) => (
            <div key={stat.label} className="px-2 py-1 md:px-3">
              <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">
                {stat.value}
              </p>
              <p className="mt-1 text-sm font-medium text-foreground-800">{stat.label}</p>
              <p className="mt-1 text-[11px] text-foreground-400">{stat.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1280px] gap-10 px-4 py-12 md:px-6 md:py-14 lg:grid-cols-[1fr_320px] lg:px-10">
        <div className="min-w-0 space-y-12">
          <section>
            <SectionHeading
              kicker="Episode Notes"
              title="剧情精选"
              description="按章节顺序整理的关键情节，包含剧情摘要、看点提示与人物关系变化。"
              actionLabel="全部剧情"
              actionTo={dramaCategory}
            />
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {featuredDramas.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-100/70 px-4 py-3">
              <p className="text-xs text-foreground-600">
                <i className="ri-information-line mr-1.5 text-sm text-accent-700" />
                在线观看功能敬请期待，当前站点提供剧情文字介绍与资料整理。
              </p>
              <Link
                to={dramaCategory}
                className="whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                浏览完整章节
              </Link>
            </div>
          </section>

          <section>
            <SectionHeading
              kicker="Cast"
              title="演员资料速览"
              description="从主要角色到特别出演，六位演员的表演笔记与角色解读。"
              actionLabel="全部演员"
              actionTo={actorCategory}
            />
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {featuredActors.map((actor) => (
                <ActorCard key={actor.id} actor={actor} />
              ))}
            </div>
          </section>

          <section>
            <SectionHeading
              kicker="Newsroom"
              title="影视资讯动态"
              description="收视表现、主创专访与幕后花絮，掌握剧集的每一条最新动态。"
              actionLabel="全部资讯"
              actionTo={newsCategory}
            />
            <div className="mt-6 rounded-lg border border-background-200/80 bg-background-50 px-4 md:px-5">
              {featuredNews.map((item) => (
                <ContentListLink key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">站内搜索</h2>
            <p className="mt-1.5 text-xs text-foreground-500">
              输入剧情标题、演员姓名或关键词
            </p>
            <div className="mt-3">
              <SearchField />
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {hotKeywords.map((keyword) => (
                <Link
                  key={keyword}
                  to={`/search?q=${encodeURIComponent(keyword)}`}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-900"
                >
                  {keyword}
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">剧集推荐</h2>
              <i className="ri-fire-line text-base text-accent-600" />
            </div>
            <ol className="mt-3 space-y-3">
              {ranking.map((item, index) => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`} className="group flex items-start gap-3">
                    <span
                      className={`font-heading text-lg font-bold leading-none ${
                        index === 0 ? "text-primary-500" : "text-background-400"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-400">
                        {formatDate(item.date)} · {item.views}次阅读
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-100/80 p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-background-200">
                <img
                  src={siteInfo.author.avatar}
                  alt={`${siteInfo.author.name} - 站长头像`}
                  title="站长 苏念"
                  className="h-full w-full object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold text-foreground-950">
                  {siteInfo.author.name}
                </p>
                <p className="mt-0.5 text-[11px] text-foreground-500">{siteInfo.author.title}</p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-foreground-600">
              {siteInfo.author.bio}
            </p>
            <Link
              to="/about"
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              了解本站
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>

          <div className="rounded-lg bg-primary-900 p-5 text-background-100">
            <p className="text-[10px] uppercase tracking-[0.24em] text-accent-300">观看提示</p>
            <p className="mt-2 font-heading text-sm font-semibold text-background-50">
              暂无播放接口演示
            </p>
            <p className="mt-2 text-xs leading-relaxed text-background-100/65">
              本站仅提供剧情介绍、演员资料与资讯内容，不提供视频播放、下载与付费服务。正式观看请前往官方播出平台。
            </p>
          </div>
        </aside>
      </div>

      <section className="border-y border-background-200/70 bg-background-100">
        <div className="mx-auto grid w-full max-w-[1280px] gap-8 px-4 py-14 md:px-6 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:px-10 lg:py-16">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-accent-700">
              About this site
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              关于「嫂子的诱惑」内容站
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {siteInfo.intro}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                了解我们
                <i className="ri-arrow-right-line text-base" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-line text-base" />
                联系编辑部
              </Link>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { icon: "ri-file-text-line", title: "逐集剧情整理", text: "每集摘要、看点与人物关系提示" },
              { icon: "ri-user-star-line", title: "演员档案", text: "角色解读与代表作品一览" },
              { icon: "ri-newspaper-line", title: "资讯追踪", text: "收视、专访与幕后花絮" },
            ].map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${feature.icon} text-base`} />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{feature.title}</p>
                  <p className="mt-1 text-xs text-foreground-500">{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}